import { useEffect, useMemo, useState } from 'react';
import { useLandingPageContext } from '@/features/campaigns/context/LandingPageContext';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import type { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { AuthModal } from '@/features/auth/components/AuthModal';
import { TravelDatePicker } from '@/shared/components/TravelDatePicker';
import { LiveCitySelect } from '@/features/destinations/components/LiveCitySelect';
import { useLiveCities } from '@/features/destinations/hooks/useLiveCities';
import { searchDropdownMenuProps } from '@/shared/constants/dropdownMenuProps';
import { HeroBackground } from '../hero/HeroBackground';
import { FESTIVE, HERO_LANGUAGE_OPTIONS } from '../hero/hero.constants';
import { keyframes } from '@mui/material/styles';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';

const NAVBAR_HEIGHT = { xs: 64, md: 72 };
const SEARCH_MAX_WIDTH = 900;
/** The search box itself sits narrower than the hero copy column. */
const SEARCH_BAR_MAX_WIDTH = 760;

interface SearchValues {
  city?: string;
  date?: string;
  language?: string;
}

function buildHeroStats(liveCityCount: number) {
  return [
    {
      icon: LocationOnOutlinedIcon,
      label: liveCityCount > 0 ? `${liveCityCount} Live Cities` : 'Live Cities',
    },
    { icon: VerifiedUserOutlinedIcon, label: 'Verified Local Guides' },
    { icon: LockOutlinedIcon, label: 'Secure Booking' },
  ];
}

const glassDivider = 'rgba(245, 196, 81, 0.2)';
const glassText = '#FFFFFF';
const glassMuted = 'rgba(255, 255, 255, 0.72)';
const glassChevron = 'rgba(255, 255, 255, 0.65)';

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
`;

/** Staggered entrance; skipped entirely for reduced-motion users. */
function entranceSx(delayMs: number) {
  return {
    '@media (prefers-reduced-motion: no-preference)': {
      animation: `${riseIn} 700ms cubic-bezier(0.22, 1, 0.36, 1) ${delayMs}ms both`,
    },
  };
}

function getDefaultTravelDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
}

function buildGuidesUrl(values: SearchValues): string {
  const params = new URLSearchParams();
  if (values.city) params.set('city', values.city);
  if (values.date) params.set('date', values.date);
  if (values.language) params.set('language', values.language);
  const qs = params.toString();
  return qs ? `/guides?${qs}` : '/guides';
}

function SearchField({
  icon,
  label,
  children,
  showDivider = true,
  isFirstField = false,
  isLastField = false,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  showDivider?: boolean;
  /** Extra left inset on desktop so content clears the pill's rounded end. */
  isFirstField?: boolean;
  isLastField?: boolean;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: { xs: 2.25, md: 1.75 },
        pl: isFirstField ? { md: 3 } : undefined,
        py: { xs: 1.5, md: 1 },
        flex: 1,
        minWidth: 0,
        width: { xs: '100%', md: 'auto' },
        borderRight: showDivider ? { md: `1px solid ${glassDivider}` } : undefined,
        borderBottom: {
          xs: isLastField ? 'none' : `1px solid ${glassDivider}`,
          md: 'none',
        },
        transition: 'background-color 180ms ease',
        '&:hover': {
          bgcolor: 'rgba(245, 196, 81, 0.06)',
        },
      }}
    >
      <Box
        sx={{
          color: FESTIVE.gold,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          width: 20,
          opacity: 0.9,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '0.625rem',
            fontWeight: 500,
            color: glassMuted,
            mb: 0.25,
            lineHeight: 1.2,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
        >
          {label}
        </Typography>
        {children}
      </Box>
    </Box>
  );
}

export function HeroSection() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { setSelectedCity, setAuthModalOpen: setLandingAuthModalOpen } = useLandingPageContext();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [pendingSearch, setPendingSearch] = useState<SearchValues | null>(null);
  const { cities: liveCities, isLoading: liveCitiesLoading } = useLiveCities();
  const heroStats = useMemo(() => buildHeroStats(liveCities.length), [liveCities.length]);

  const defaultDate = useMemo(() => getDefaultTravelDate(), []);
  const today = useMemo(() => new Date().toISOString().split('T')[0], []);

  const [destination, setDestination] = useState('');
  const [date, setDate] = useState(defaultDate);
  const [language, setLanguage] = useState('English');

  useEffect(() => {
    setSelectedCity(destination.trim() ? destination.trim() : null);
  }, [destination, setSelectedCity]);

  useEffect(() => {
    setLandingAuthModalOpen(authModalOpen);
  }, [authModalOpen, setLandingAuthModalOpen]);

  const navigateToGuides = (values: SearchValues) => {
    if (isAuthenticated) {
      navigate(buildGuidesUrl(values));
    } else {
      setPendingSearch(values);
      setAuthModalOpen(true);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigateToGuides({
      city: destination || undefined,
      date: date || undefined,
      language: language || undefined,
    });
  };

  const handleCityChipClick = (cityName: string) => {
    navigateToGuides({
      city: cityName,
      date: date || undefined,
      language: language || undefined,
    });
  };

  const handleAuthSuccess = (route: string) => {
    const isTravelerGuidesRoute =
      route === '/' || route === '/guides' || route.startsWith('/guides?');

    if (pendingSearch && isTravelerGuidesRoute) {
      navigate(buildGuidesUrl(pendingSearch));
    } else {
      navigate(route, { replace: true });
    }
    setPendingSearch(null);
  };

  const scrollToExplore = () => {
    document.getElementById('explore-experiences')?.scrollIntoView({ behavior: 'smooth' });
  };

  const languageLabel =
    HERO_LANGUAGE_OPTIONS.find((o) => o.value === language)?.label ?? 'Any language';

  const fieldValueSx = {
    fontSize: { xs: '0.875rem', md: '0.9375rem' },
    fontWeight: 600,
    color: glassText,
    lineHeight: 1.3,
  };

  const selectSx = {
    width: '100%',
    ...fieldValueSx,
    '& .MuiSelect-select': {
      p: '0 !important',
      pr: '28px !important',
      display: 'flex',
      alignItems: 'center',
      minHeight: '22px !important',
    },
    '& .MuiSelect-icon': {
      color: glassChevron,
      right: 0,
      fontSize: 22,
    },
  };

  const searchBar = (
    <Box
      component="form"
      onSubmit={handleSearch}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'stretch',
        width: '100%',
        boxSizing: 'border-box',
        bgcolor: FESTIVE.navyGlass,
        backdropFilter: 'blur(18px) saturate(140%)',
        WebkitBackdropFilter: 'blur(18px) saturate(140%)',
        border: `1px solid ${FESTIVE.goldBorder}`,
        borderRadius: { xs: '20px', md: '999px' },
        boxShadow:
          '0 20px 48px rgba(4, 8, 28, 0.45), inset 0 1px 0 rgba(255, 231, 163, 0.12)',
        overflow: 'hidden',
        transition: 'border-color 200ms ease, box-shadow 200ms ease',
        '&:focus-within': {
          borderColor: FESTIVE.goldBorderStrong,
          boxShadow:
            '0 20px 48px rgba(4, 8, 28, 0.45), 0 0 0 3px rgba(245, 196, 81, 0.18), inset 0 1px 0 rgba(255, 231, 163, 0.12)',
        },
      }}
    >
      <SearchField
        icon={<PlaceOutlinedIcon sx={{ fontSize: 20 }} />}
        label="Destination"
        isFirstField
      >
        <LiveCitySelect
          value={destination}
          onChange={setDestination}
          placeholder="Where to?"
          variant="hero"
        />
      </SearchField>

      <SearchField icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 20 }} />} label="Travel Date">
        <TravelDatePicker
          value={date}
          onChange={setDate}
          minDate={today}
          displaySx={fieldValueSx}
          showChevron
          chevronSx={{ color: glassChevron }}
        />
      </SearchField>

      <SearchField
        icon={<LanguageOutlinedIcon sx={{ fontSize: 20 }} />}
        label="Language"
        showDivider={false}
        isLastField
      >
        <Select
          value={language}
          onChange={(e: SelectChangeEvent) => setLanguage(e.target.value)}
          variant="standard"
          disableUnderline
          displayEmpty
          IconComponent={KeyboardArrowDownRoundedIcon}
          renderValue={() => <Typography sx={fieldValueSx}>{languageLabel}</Typography>}
          MenuProps={searchDropdownMenuProps}
          sx={selectSx}
        >
          {HERO_LANGUAGE_OPTIONS.map(({ value, label }) => (
            <MenuItem key={value || 'any'} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </SearchField>

      {/* Mobile: full-width CTA. Desktop: compact circular icon. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          width: { xs: '100%', md: 'auto' },
          px: { xs: 2, md: 1 },
          pt: { xs: 0.5, md: 0.75 },
          pb: { xs: 2, md: 0.75 },
          borderTop: { xs: `1px solid ${glassDivider}`, md: 'none' },
          bgcolor: { xs: 'rgba(4, 8, 28, 0.25)', md: 'transparent' },
        }}
      >
        <Button
          type="submit"
          aria-label="Search guides"
          variant="contained"
          startIcon={<SearchIcon sx={{ fontSize: 22 }} />}
          sx={{
            display: { xs: 'inline-flex', md: 'none' },
            width: '100%',
            minHeight: 48,
            px: 2.5,
            py: 1.5,
            borderRadius: '999px',
            textTransform: 'none',
            fontSize: '0.9375rem',
            fontWeight: 700,
            letterSpacing: '0.01em',
            background: `linear-gradient(135deg, ${FESTIVE.gold} 0%, ${FESTIVE.goldDeep} 100%)`,
            color: '#1A1405',
            boxShadow: '0 8px 20px rgba(224, 154, 43, 0.35)',
            '&:hover': {
              background: `linear-gradient(135deg, #FFD56E 0%, ${FESTIVE.gold} 100%)`,
              boxShadow: '0 10px 24px rgba(224, 154, 43, 0.5)',
            },
            '&:active': {
              transform: 'scale(0.99)',
            },
            transition: 'background-color 180ms ease, box-shadow 180ms ease, transform 180ms ease',
          }}
        >
          Search guides
        </Button>

        <IconButton
          type="submit"
          aria-label="Search guides"
          sx={{
            display: { xs: 'none', md: 'inline-flex' },
            width: 44,
            height: 44,
            background: `linear-gradient(135deg, ${FESTIVE.gold} 0%, ${FESTIVE.goldDeep} 100%)`,
            color: '#1A1405',
            boxShadow: '0 8px 20px rgba(224, 154, 43, 0.4)',
            '&:hover': {
              background: `linear-gradient(135deg, #FFD56E 0%, ${FESTIVE.gold} 100%)`,
              boxShadow: '0 10px 24px rgba(224, 154, 43, 0.55)',
              transform: 'scale(1.04)',
            },
            '&:active': {
              transform: 'scale(0.98)',
            },
            transition: 'all 180ms ease',
          }}
        >
          <SearchIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <>
      <Box
        component="section"
        id="hero"
        sx={{
          position: 'relative',
          width: '100%',
          // Since the section is pulled up under the fixed navbar via a negative
          // top margin, its height must include that offset so the bottom edge
          // still lands exactly at the viewport bottom (full-viewport coverage).
          minHeight: {
            xs: `calc(100dvh + ${NAVBAR_HEIGHT.xs}px)`,
            md: `calc(100dvh + ${NAVBAR_HEIGHT.md}px)`,
          },
          mt: { xs: `-${NAVBAR_HEIGHT.xs}px`, md: `-${NAVBAR_HEIGHT.md}px` },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <HeroBackground />

        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            zIndex: 2,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            // Mobile: top-align so a tall stack never centers under the fixed logo.
            // Desktop: keep the centered full-bleed composition.
            justifyContent: { xs: 'flex-start', md: 'center' },
            width: '100%',
            boxSizing: 'border-box',
            // Section is pulled under the fixed navbar via mt: -NAVBAR.
            // pt must offset that pull (NAVBAR) + clear the bar (NAVBAR) + breathing room.
            // Theme spacing is 4px/unit — prefer raw px for these critical insets.
            pt: {
              xs: `calc(${NAVBAR_HEIGHT.xs * 2}px + env(safe-area-inset-top, 0px) + 32px)`,
              sm: `calc(${NAVBAR_HEIGHT.xs * 2}px + env(safe-area-inset-top, 0px) + 36px)`,
              md: `calc(${NAVBAR_HEIGHT.md * 2}px + 40px)`,
            },
            // Room for curve + scroll button so content never overlaps
            pb: { xs: 14, sm: 16, md: 18 },
            px: { xs: 6, sm: 6, md: 6 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 5, sm: 7, md: 8 },
              width: '100%',
              maxWidth: SEARCH_MAX_WIDTH,
            }}
          >
            {/* Copy */}
            <Box sx={{ maxWidth: { xs: '100%', sm: 600, md: 660 } }}>
              <Typography
                component="h1"
                sx={{
                  fontFamily: '"Playfair Display", Georgia, serif',
                  fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3.25rem' },
                  fontWeight: 700,
                  color: '#FFFFFF',
                  lineHeight: { xs: 1.22, md: 1.12 },
                  letterSpacing: '-0.02em',
                  mb: { xs: 3, md: 4 },
                  textShadow: '0 2px 24px rgba(4, 8, 28, 0.55)',
                  overflowWrap: 'break-word',
                }}
              >
                <Box component="span" sx={{ display: 'block', ...entranceSx(0) }}>
                  Celebrate Dev Deepawali in Kashi
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    mt: { xs: 0.5, md: 0.75 },
                    fontSize: { xs: '1.375rem', sm: '1.875rem', md: '2.375rem' },
                    lineHeight: 1.2,
                    ...entranceSx(120),
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      background: FESTIVE.goldGradient,
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text',
                      color: 'transparent',
                      WebkitTextFillColor: 'transparent',
                      filter: 'drop-shadow(0 2px 14px rgba(240, 162, 59, 0.35))',
                    }}
                  >
                    Experience the Magic of Varanasi
                  </Box>
                </Box>
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: '0.9375rem', md: '1.0625rem' },
                  fontWeight: 400,
                  color: 'rgba(255, 255, 255, 0.9)',
                  lineHeight: { xs: 1.6, md: 1.7 },
                  maxWidth: { xs: '100%', sm: 540, md: 560 },
                  textShadow: '0 1px 12px rgba(4, 8, 28, 0.5)',
                  ...entranceSx(240),
                }}
              >
                Witness the spectacular Ganga Aarti, thousands of glowing diyas, magnificent
                fireworks and the spiritual beauty of Kashi. Explore unforgettable experiences with
                verified local guides.
              </Typography>
            </Box>

            {/* Search */}
            <Box sx={{ width: '100%', maxWidth: SEARCH_BAR_MAX_WIDTH, ...entranceSx(360) }}>
              {searchBar}
            </Box>

            {/* Live city quick picks */}
            <Box sx={entranceSx(480)}>
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  color: glassMuted,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  mb: 1.25,
                }}
              >
                Browse live cities
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: { xs: 'nowrap', md: 'wrap' },
                  gap: 1,
                  overflowX: { xs: 'auto', md: 'visible' },
                  WebkitOverflowScrolling: 'touch',
                  mx: { xs: -0.5, md: 0 },
                  px: { xs: 0.5, md: 0 },
                  pb: { xs: 0.5, md: 0 },
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }}
              >
                {liveCitiesLoading &&
                  Array.from({ length: 5 }, (_, index) => (
                    <Skeleton
                      key={index}
                      variant="rounded"
                      width={84}
                      height={32}
                      sx={{ flexShrink: 0, borderRadius: '999px', bgcolor: 'rgba(245, 196, 81, 0.14)' }}
                    />
                  ))}
                {liveCities.map(({ name }) => (
                  <Chip
                    key={name}
                    label={name}
                    clickable
                    onClick={() => handleCityChipClick(name)}
                    sx={{
                      height: 32,
                      flexShrink: 0,
                      borderRadius: '999px',
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      bgcolor: 'rgba(8, 15, 40, 0.42)',
                      color: '#FFFFFF',
                      border: `1px solid ${FESTIVE.goldBorder}`,
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)',
                      transition: 'all 180ms ease',
                      '&:hover': {
                        bgcolor: 'rgba(245, 196, 81, 0.16)',
                        borderColor: FESTIVE.goldBorderStrong,
                        color: '#FFE7A3',
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* Stats */}
            <Box
              sx={{
                ...entranceSx(600),
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(3, minmax(0, auto))',
                },
                gap: { xs: 3, sm: 5, md: 7 },
                alignItems: 'center',
                justifyContent: { sm: 'start' },
              }}
            >
              {heroStats.map(({ icon: Icon, label }) => (
                <Box
                  key={label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 36, md: 40 },
                      height: { xs: 36, md: 40 },
                      borderRadius: '10px',
                      bgcolor: 'rgba(8, 15, 40, 0.5)',
                      border: `1px solid ${FESTIVE.goldBorder}`,
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: '0 4px 14px rgba(240, 162, 59, 0.18)',
                    }}
                  >
                    <Icon
                      sx={{
                        fontSize: { xs: 18, md: 20 },
                        color: '#FFFFFF',
                        filter: 'drop-shadow(0 0 6px rgba(245, 196, 81, 0.55))',
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: { xs: '0.8125rem', md: '0.9375rem' },
                      fontWeight: 600,
                      color: '#FFFFFF',
                      lineHeight: 1.35,
                      textShadow: '0 1px 8px rgba(15, 23, 42, 0.3)',
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Container>

        {/* Scroll cue — above the curve */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            bottom: { xs: 40, md: 56 },
            transform: 'translateX(-50%)',
            zIndex: 3,
          }}
        >
          <IconButton
            aria-label="Scroll to experiences"
            onClick={scrollToExplore}
            sx={{
              width: { xs: 40, md: 44 },
              height: { xs: 40, md: 44 },
              bgcolor: 'rgba(255, 255, 255, 0.96)',
              color: FESTIVE.goldDeep,
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.18)',
              '&:hover': {
                bgcolor: '#FFFFFF',
                transform: 'translateY(2px)',
              },
              transition: 'transform 200ms ease, background-color 200ms ease',
            }}
          >
            <KeyboardArrowDownRoundedIcon />
          </IconButton>
        </Box>

        {/* Curved bottom */}
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: -1,
            height: { xs: 48, md: 72 },
            zIndex: 2,
            pointerEvents: 'none',
            lineHeight: 0,
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 1440 72"
            preserveAspectRatio="none"
            sx={{ width: '100%', height: '100%', display: 'block' }}
          >
            <path d="M0,28 C360,72 1080,0 1440,36 L1440,72 L0,72 Z" fill="#FFFFFF" />
          </Box>
        </Box>
      </Box>

      <AuthModal
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onAuthSuccess={handleAuthSuccess}
        travelerSearchFlow
        title="Sign in to find guides"
        subtitle="Create a free account or sign in to explore verified local guides."
      />
    </>
  );
}
